import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  ChevronRight, 
  ArrowRight, 
  Scale, 
  ShieldCheck, 
  Calendar,
  Layers,
  CheckCircle2
} from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import PracticeAreaCatalogModal from '../../components/common/PracticeAreaCatalogModal';
import InstantBookingModal from '../../components/booking/InstantBookingModal';
import { PRACTICE_TOPICS } from '../../data/practiceTopics';

export default function ServicesPage() {
  const { t, localized, currentLang } = useLanguage();
  const [services, setServices] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedLawyerForBooking, setSelectedLawyerForBooking] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/services'),
      api.get('/lawyers'),
    ]).then(([sRes, lRes]) => {
      if (sRes?.success) setServices(sRes.services || []);
      if (lRes?.success) setLawyers(lRes.lawyers || []);
    }).finally(() => setLoading(false));
  }, []);

  const handleOpenCatalog = (service) => {
    const topicMatch = PRACTICE_TOPICS.find(
      (t) =>
        t.id === service.id ||
        t.titleUz?.toLowerCase() === (service.titleUz || service.title)?.toLowerCase() ||
        t.id === service.slug
    );

    if (topicMatch) {
      setSelectedTopic(topicMatch);
    } else {
      setSelectedTopic({
        id: service.id,
        title: localized(service, 'title'),
        description: localized(service, 'fullDesc') || localized(service, 'shortDesc'),
        items: [
          `${localized(service, 'title')} bo‘yicha to‘liq sud himoyasi va vakillik`,
          'Shartnomalar va bitimlarni chuqur huquqiy ekspertizadan o‘tkazish',
          'Davlat va soliq idoralari bilan bog‘liq nizolarni hal qilish',
          'Zararlar va kompensatsiyalarni to‘liq hajmda undirish',
          'Kompaniyalar va fuqarolar uchun doimiy yuridik maslahat'
        ]
      });
    }
    setCatalogModalOpen(true);
  };

  const handleBookFromCatalog = (topic) => {
    setSelectedTopic(topic);
    setBookingModalOpen(true);
  };

  return (
    <div className="bg-[#05080e] min-h-screen text-slate-200">
      
      {/* Header Banner */}
      <section className="relative bg-[#070c16] py-16 sm:py-24 border-b border-[#141d2d] overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#c59b27]/10 blur-[120px] rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:underline mb-4"
          >
            &larr; {currentLang === 'uz' ? 'Bosh sahifaga qaytish' : currentLang === 'ru' ? 'Назад на главную' : 'Back to Home'}
          </Link>
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
            PRACTICE AREAS & LEGAL DEFENSE
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-slate-100">
            Yuridik Amaliyot Katalogi
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-4 font-light leading-relaxed">
            Har bir soha bo‘yicha ixtisoslashgan yetakchi advokatlarimiz strategik himoyani kafolatlaydi. O‘zingizga kerakli sohani tanlang va to‘liq katalog bilan tanishing.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-24 bg-[#05080e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20 text-slate-400">Yuklanmoqda...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-[#090e18] rounded-xl border border-[#1b2b46] hover:border-[#c59b27]/80 p-7 shadow-xl hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c59b27]/15 transition-all duration-300 flex flex-col justify-between group relative overflow-hidden"
                >
                  <div className="absolute -top-10 -right-10 w-28 h-28 bg-[#c59b27]/0 group-hover:bg-[#c59b27]/15 rounded-full blur-2xl transition-all duration-500 pointer-events-none" />
                  <div>
                    {/* Top Icon & Badge */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-12 h-12 rounded-lg bg-[#111a2c] border border-[#c59b27]/30 flex items-center justify-center text-[#c59b27] group-hover:border-[#c59b27] group-hover:bg-[#182742] group-hover:scale-110 transition-all duration-300">
                        <Briefcase className="w-6 h-6" />
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-[#c59b27] font-semibold px-2.5 py-1 rounded bg-[#c59b27]/10 border border-[#c59b27]/20 group-hover:bg-[#c59b27]/20 transition-colors">
                        Amaliyot
                      </span>
                    </div>

                    <h3 className="font-serif text-xl font-bold text-slate-100 mb-3 group-hover:text-amber-200 transition-colors line-clamp-2 min-h-[56px] flex items-center">
                      {localized(service, 'title')}
                    </h3>
                    <p className="text-slate-400 text-xs leading-relaxed mb-6 font-light line-clamp-3 min-h-[50px]">
                      {localized(service, 'shortDesc')}
                    </p>
                  </div>

                  <div className="pt-5 border-t border-[#141d2d] flex items-center justify-between">
                    <button
                      onClick={() => handleOpenCatalog(service)}
                      className="text-xs font-semibold text-slate-300 hover:text-[#c59b27] inline-flex items-center transition-colors cursor-pointer"
                    >
                      <span>Katalogni ochish</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>

                    <button
                      onClick={() => {
                        setSelectedTopic({ id: service.id, title: localized(service, 'title') });
                        setBookingModalOpen(true);
                      }}
                      className="px-3.5 py-1.5 rounded bg-[#b38038] hover:bg-[#c59b27] text-white text-[11px] font-bold uppercase tracking-wider transition-colors cursor-pointer shadow-sm"
                    >
                      Uchrashuv
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Catalog Modal */}
      <PracticeAreaCatalogModal
        isOpen={catalogModalOpen}
        onClose={() => setCatalogModalOpen(false)}
        topic={selectedTopic}
        onBookTopic={handleBookFromCatalog}
        onBookLawyer={(lawyer) => {
          setCatalogModalOpen(false);
          setSelectedLawyerForBooking(lawyer);
          setSelectedTopic({ id: lawyer.specialization, title: lawyer.specialization });
          setBookingModalOpen(true);
        }}
        lawyers={lawyers}
      />

      {/* Instant Booking Modal */}
      <InstantBookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedLawyerForBooking(null);
        }}
        preSelectedTopic={selectedTopic}
        initialLawyerId={selectedLawyerForBooking?.id}
      />

    </div>
  );
}
